/**
 * The call, as one object. Everything else in this directory is a part of it.
 *
 * IT IS NOT A REACT HOOK, and that is deliberate. A call outlives any single
 * render, holds two WebSockets, two workers, a peer connection and an
 * AudioContext, and must survive a component re-rendering for reasons that have
 * nothing to do with it. So this is a plain object with `subscribe` /
 * `getSnapshot`, which is exactly what `useSyncExternalStore` consumes — the page
 * gets to be a pure function of a snapshot, and the call gets to not care about
 * React's lifecycle. A `useEffect`-driven version of this file was the first
 * thing tried and it double-started every call in development, because Strict
 * Mode mounts twice and a WebRTC offer is not idempotent.
 *
 * THE START SEQUENCE, IN ORDER, AND WHY THAT ORDER:
 *
 *   1. probe()            — refuse iOS before asking for a microphone.
 *   2. POST /api/sessions ∥ getUserMedia — CONCURRENT, deliberately: the session
 *                          create sits behind Neon, the DO and the customer's
 *                          init webhook (up to 3s), the mic behind device
 *                          acquisition — serial, the caller paid for both. See
 *                          the comment at the call site for the trade.
 *   4. AudioContext.resume() — INSIDE THE GESTURE. A context created and resumed
 *                          later is suspended forever on Chrome, the worklet
 *                          never runs, and it presents as a silent recording.
 *   5. recorder.start()   — spawn the workers now, so the transform worker is
 *                          already live when a transform is installed on it.
 *   6. peer + transforms  — no data channel, ever. See transport.ts.
 *   7. SDP exchange       — one POST, one answer.
 *   8. /control then /media — the control socket's `ready` carries the recorder's
 *                          resume point, so it has to come first.
 *
 * WHAT MUST NEVER HAPPEN HERE: an error in steps 5 or 8 ending the call. Media is
 * browser<->OpenAI. A recorder that fails to start, a control socket that never
 * connects, a DO that gets deployed mid-call — all of those degrade the session
 * and none of them are allowed to touch the conversation. Every catch in this
 * file that sits downstream of `setRemoteDescription` records a notice and
 * carries on. That property is the reason the architecture exists; this file is
 * where it is either honoured or quietly thrown away.
 *
 * ONE EXCEPTION, AND IT GOES THE OTHER WAY: A TAKEOVER. When a person in the
 * panel takes the call (docs/hitl.md), the DO hangs up the provider's leg and the
 * caller hears the person over the live-audio socket instead. From then on the
 * peer connection failing is expected and must NOT end the call, while the live
 * socket is what the conversation depends on. `onTakeover` and `onPcState` hold
 * that rule.
 */
import type { ClientMsg, ServerMsg, RecordingDiagnostic, RecordingDiagnosticCode } from '@voiceai/wire'
import { recordingErrorName, parseRecordingDiagnostic } from '@voiceai/wire'
import { ApiError } from '@/api'
import { panelBackend } from './panel-backend'
import type { CallBackend } from './backend'
import { ControlChannel, nowEpochMs, socketUrl } from './control'
import { AudioMeter } from './meter'
import { Recorder } from './recorder'
import { Telemetry } from './telemetry'
import { probe } from './support'
import type { SupportReport } from './support'
import {
  createPeer,
  makeOffer,
  openMicrophone,
  teardown,
} from './transport'
import { GREETING_CLIP_DEADLINE_MS, GreetingClip } from './greeting-clip'
import type { ClipPlayback, GreetingClipReport } from './greeting-clip'
import { LiveLink } from './live/link'
import { mergeTranscriptLine } from './transcript-merge'
import type { CallSnapshot, CardActivity, RecorderCounters, ToolActivity, TrackName } from './types'
import { emptyCounters } from './types'

export type { CallSnapshot, CallPhase, CardActivity, RecorderCounters, StartStage, TranscriptLine, ToolActivity } from './types'
export type { SupportReport } from './support'
export { probe } from './support'

/** How often client telemetry is shipped. Four seconds is two stats samples. */
const TELEMETRY_FLUSH_MS = 4000
/** How long a control outage must last before the caller is told about it.
 *  Under this, a drop is a blip the first re-dials usually heal (backoff starts
 *  at 250ms) and the caller notices nothing; past it, the transcript has
 *  visibly stalled and silence from us reads worse than the sentence. */
const CONTROL_DROP_NOTICE_MS = 4000
/** The provider's hard session cap. We warn before it and end before it, because
 *  the alternative is the model going silent mid-sentence with no explanation. */
const SESSION_CAP_MS = 60 * 60 * 1000
const CAP_WARN_MS = SESSION_CAP_MS - 5 * 60 * 1000
const CAP_END_MS = SESSION_CAP_MS - 30 * 1000
/** Keep the transcript bounded. An hour of conversation is a few hundred items;
 *  this is a guard against a pathological stream, not a product decision. */
const MAX_TRANSCRIPT_LINES = 600
/** How long the recorder may drain during `end()` before the UI says why the
 *  button is still spinning. Long enough that a normal teardown never shows it. */
const TAIL_NOTICE_MS = 2500

export interface CallOptions {
  agentId: string
  label?: string
  /**
   * Client-asserted key/value facts (`userID`, `ticket`, …) for the agent's
   * init webhook and HTTP tools — the customer's own systems, the only parties
   * that can judge them. GATHERED BY THE CALLER AT CLICK TIME, not at mount:
   * the page's URL (or whatever else feeds this) is read the moment the person
   * presses start, so a value that arrived after render still makes the call.
   * Sent once, in the createSession body; the server bounds and never trusts
   * them (see `CreateSessionRequest.params` in @voiceai/wire).
   */
  params?: Record<string, string>
  /** Must be in the document. See transport.ts for why. */
  audioEl: HTMLAudioElement
  /**
   * `greeting_render`: the agent says its saved greeting once and the server
   * stores it as the agent's greeting clip, then ends the call. The outcome
   * arrives as `snapshot.greetingRender`. Panel only.
   */
  purpose?: 'greeting_render'
  /**
   * How to reach the server. Defaults to the control panel's authenticated API.
   *
   * The embeddable widget passes its own, because it runs on somebody else's
   * website with no cookie — see backend.ts for why this is a parameter rather than
   * a second copy of this file.
   */
  backend?: CallBackend
}

const INITIAL: CallSnapshot = {
  phase: 'idle',
  startStage: null,
  greetingRender: null,
  sessionId: null,
  serverState: null,
  serverStateDetail: null,
  startedAtMs: null,
  endedAtMs: null,
  error: null,
  notices: [],
  transcript: [],
  tools: [],
  cards: [],
  speaking: { user: false, assistant: false },
  muted: false,
  recorderPath: 'none',
  counters: emptyCounters(),
  stats: null,
  clock: null,
  pc: { connection: null, ice: null },
  controlUp: false,
  turns: [],
  takenOver: false,
}

/**
 * The server states that end the call on this page. `ENDED_TAKEN_OVER` is here
 * because a call a person held ends like any other; `TAKEN_OVER` is NOT, because
 * it is live — the caller is still talking, to a person now.
 */
const ENDS_THE_CALL: ReadonlySet<string> = new Set(['ENDED_NORMAL', 'ENDED_UNKNOWN', 'FAILED', 'ENDED_TAKEN_OVER'])

export class CallSession {
  private snap: CallSnapshot = INITIAL
  private listeners = new Set<() => void>()
  /** Set on `start`. Defaults so `end()` before any `start()` cannot throw. */
  private backend: CallBackend = panelBackend
  /**
   * Loudness for anything that draws. Deliberately NOT part of the snapshot — a
   * value that changes at frame rate must not drive React re-renders. See meter.ts.
   */
  private meter: AudioMeter | null = null

  private control: ControlChannel | null = null
  private recorder: Recorder | null = null
  /** One observation per code, queued until control is ready (including reconnects). */
  private readonly recorderDiagnostics = new Map<RecordingDiagnosticCode, RecordingDiagnostic>()
  private readonly seenRecorderDiagnostics = new Set<RecordingDiagnosticCode>()
  private telemetry: Telemetry | null = null
  private pc: RTCPeerConnection | null = null
  private mic: MediaStream | null = null
  /** The pre-recorded greeting, when this call opens with one. */
  private greetingClip: GreetingClip | null = null
  /** True while the clip plays: the caller's mic is held silent (see
   *  greeting-clip.ts), on top of whatever the human's own mute says. */
  private greetingHold = false
  /** Greeting reports made before the control channel was ready, in order. */
  private greetingOutbox: ClientMsg[] = []
  private ctx: AudioContext | null = null
  /** The agent's `<audio>`, kept so a takeover can mute it and `end` can undo that. */
  private audioEl: HTMLAudioElement | null = null
  private audioElWasMuted = false
  /**
   * Where the live-audio socket goes, with the reconnect token already in it,
   * or null when the server offered none (an older edge-api). See docs/hitl.md.
   */
  private liveUrl: string | null = null
  /** Open while somebody in the panel is listening; see live/link.ts. */
  private live: LiveLink | null = null
  /** The agent's stream, kept for a live tap that starts after `ontrack`. */
  private remoteStream: MediaStream | null = null
  private liveRefusalLogged = false
  private flushTimer: ReturnType<typeof setInterval> | null = null
  /** Armed on a control drop, fires the user-visible notice only if the outage
   *  OUTLIVES the grace period. See the onUp hook for the reasoning. */
  private controlDropTimer: ReturnType<typeof setTimeout> | null = null
  /** True once the current outage has been announced, so reconnect can say so
   *  — and so a flapping socket announces once, not per failed re-dial. */
  private controlDropAnnounced = false
  private capTimers: ReturnType<typeof setTimeout>[] = []
  private ending = false
  /** Last chunk seq we believe the server has, mirrored so `hello` can carry it
   *  on a reconnect without a round trip to the worker. */
  private lastSeq: Partial<Record<TrackName, number>> = {}

  // --------------------------------------------------------------------------
  // Store surface, for useSyncExternalStore
  // --------------------------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = (): CallSnapshot => this.snap

  /**
   * Live loudness per direction, 0..1.
   *
   * READ THIS INSIDE YOUR OWN `requestAnimationFrame` LOOP; it is deliberately not
   * in the snapshot. A number that changes sixty times a second has no business
   * re-rendering every subscribed component — and only a canvas wants it. Returns
   * zeroes when there is no AudioContext (the fallback recorder path), so a
   * visualiser must degrade to `snapshot.speaking` rather than assume movement.
   */
  levels = (nowMs?: number): { user: number; assistant: number } =>
    this.meter?.readAll(nowMs) ?? { user: 0, assistant: 0 }

  /**
   * Frequency bins for one direction, for a visualiser that draws a spectrum rather
   * than an amplitude. Same contract as `levels`: read it inside your own animation
   * frame, and treat a 0 return as "no data available", not as silence.
   *
   * It exists because both mirrored-spectrum widgets need bins rather than one
   * scalar: the ported ElevenLabs waveform and Bars. The analysers are already there
   * — this only exposes what they were computing anyway.
   */
  spectrum = (track: TrackName, out: Uint8Array): number => this.meter?.spectrum(track, out) ?? 0

  private set(patch: Partial<CallSnapshot>): void {
    this.snap = { ...this.snap, ...patch }
    for (const l of this.listeners) l()
  }

  private notice(message: string): void {
    if (this.snap.notices.includes(message)) return
    this.set({ notices: [...this.snap.notices, message] })
  }

  private log = (level: 'info' | 'warn' | 'error', message: string): void => {
    if (level === 'info') return
    // Anything a worker or a channel calls a warning is something a human
    // debugging a demo needs to see, so it goes in the UI and not just the
    // console. The console gets it too, because a screenshot of the panel is
    // easier to read than a screenshot of devtools.
    console.warn(`[call] ${message}`)
    this.notice(message)
  }

  private reportRecorderDiagnostic = (code: RecordingDiagnosticCode, errorName?: string): void => {
    const diagnostic = parseRecordingDiagnostic({ code, path: this.snap.recorderPath, errorName })
    if (!diagnostic || this.seenRecorderDiagnostics.has(code)) return
    this.seenRecorderDiagnostics.add(code)
    this.recorderDiagnostics.set(code, diagnostic)
    this.flushRecorderDiagnostics()
  }

  private flushRecorderDiagnostics(): void {
    for (const [code, diagnostic] of this.recorderDiagnostics) {
      if (!this.control?.send({ t: 'rec.diagnostic', diagnostic })) break
      this.recorderDiagnostics.delete(code)
    }
  }

  // --------------------------------------------------------------------------
  // Start
  // --------------------------------------------------------------------------

  async start(opts: CallOptions): Promise<void> {
    if (this.snap.phase === 'live' || this.snap.phase === 'starting') return
    this.recorderDiagnostics.clear()
    this.seenRecorderDiagnostics.clear()
    this.greetingClip = null
    this.greetingHold = false
    this.greetingOutbox = []
    this.liveUrl = null
    this.liveRefusalLogged = false
    this.remoteStream = null
    this.audioEl = opts.audioEl
    this.set({ ...INITIAL, phase: 'checking', startStage: 'checking' })

    // Captured once, into a field, because `end()` runs long after this function
    // returns and must reach the same server the session was created on. Reading it
    // off `opts` there would let a caller who reuses its options object end a
    // session against a different backend than the one that minted it.
    const backend = opts.backend ?? panelBackend
    this.backend = backend

    let support: SupportReport
    try {
      support = await probe()
    } catch (e) {
      this.fail(`could not check browser support: ${String(e)}`)
      return
    }
    if (!support.ok) {
      this.set({ phase: 'blocked', error: support.blocked })
      return
    }
    for (const w of support.warnings) this.notice(w)
    this.set({ phase: 'starting', recorderPath: support.recorderPath, startStage: 'security' })

    let sessionId: string
    let controlUrl: string
    let controlReconnectUrl: string
    let mediaUrl: string
    let mediaReconnectUrl: string
    let recordingWanted: boolean
    const ac = new AbortController()
    /**
     * THE SESSION AND THE MICROPHONE ARE OPENED TOGETHER, and this is the
     * single biggest click-to-greeting win on the whole start path. They used
     * to be serial — create, then prompt — and both are slow in ways that do
     * not overlap by accident: createSession sits behind Neon, the DO, and the
     * agent's init webhook (a customer endpoint with a 3-SECOND budget that a
     * slow ServiceNow instance will happily spend), while getUserMedia takes
     * hundreds of milliseconds of device acquisition even when permission is
     * already granted. Concurrent, the caller waits for the slower of the two
     * instead of the sum.
     *
     * WHAT THE OLD ORDER BOUGHT, AND WHAT IT COSTS NOW: "fail on our own
     * infrastructure before prompting" — a dead server no longer spares the
     * visitor a permission prompt. That is a rare case traded for seconds on
     * every call, and the prompt moving CLOSER to the click is itself a
     * robustness win: getUserMedia inside the click's own task cannot lose its
     * user-gesture standing to a slow await ahead of it.
     *
     * `allSettled`, so neither failure is dropped: a session with no mic is
     * ended (fire-and-forget by contract) so the server is not left holding a
     * CREATED row for the alarm to reap; a mic with no session is stopped so
     * the tab's recording indicator does not outlive this function.
     */
    const [createdR, micR] = await Promise.allSettled([
      backend.createSession(
        {
          agentId: opts.agentId,
          label: opts.label,
          params: opts.params,
          supports: { greetingClip: true },
          ...(opts.purpose ? { purpose: opts.purpose } : {}),
        },
        ac.signal,
      ),
      openMicrophone(),
    ])
    if (createdR.status === 'rejected') {
      if (micR.status === 'fulfilled') for (const t of micR.value.getTracks()) t.stop()
      const e = createdR.reason
      this.fail(
        e instanceof ApiError
          ? `the session could not be created: ${e.message}`
          : describe(e, 'the session could not be created'),
      )
      return
    }
    const created = createdR.value
    sessionId = created.sessionId
    // Two credentials, two URLs per socket. `controlToken` lasts two minutes —
    // right for opening a socket now, useless for reopening one at minute six —
    // so each channel keeps a re-dial URL carrying the long-lived reconnect
    // token and uses it for every attempt after the first.
    controlUrl = socketUrl(created.controlUrl, created.controlToken)
    controlReconnectUrl = socketUrl(created.controlUrl, created.reconnectToken)
    mediaUrl = socketUrl(created.mediaUrl, created.controlToken)
    mediaReconnectUrl = socketUrl(created.mediaUrl, created.reconnectToken)
    // The live socket is only ever opened on the DO's request, which can come at
    // any minute of the call, so it carries the long-lived token from the start.
    this.liveUrl = created.liveUrl ? socketUrl(created.liveUrl, created.reconnectToken) : null
    recordingWanted = created.recording
    this.set({ sessionId })

    if (micR.status === 'rejected') {
      const e = micR.reason
      backend.endSession(sessionId, 'mic_denied')
      this.fail(
        e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError')
          ? 'Microphone access was denied. A voice call needs the microphone; grant it in the address bar and try again.'
          : describe(e, 'the microphone could not be opened'),
      )
      return
    }
    this.mic = micR.value
    // The slow, variable leg (webhook + mic) is behind us; the rest is audio.
    this.set({ startStage: 'audio' })

    // The pre-recorded greeting, when the server named one. Created HERE —
    // after the mic exists and before the peer connection does — because it
    // holds the mic silent from its first moment, and the fetch overlaps
    // everything that follows. It plays once `audioReady` below says it can.
    if (created.greetingClip) {
      const sha256 = created.greetingClip.sha256
      this.greetingClip = new GreetingClip({
        fetch: () => backend.fetchGreetingClip(sha256, AbortSignal.timeout(GREETING_CLIP_DEADLINE_MS)),
        play: (bytes) => this.playGreetingClip(bytes),
        holdMic: (held) => {
          this.greetingHold = held
          this.applyMicEnabled()
        },
        report: (msg) => this.sendGreetingReport(msg),
        onPlaying: (playing) => this.set({ speaking: { ...this.snap.speaking, assistant: playing } }),
        setTimer: (fn, ms) => setTimeout(fn, ms),
        clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
        now: () => Date.now(),
      })
    }

    // Pinned to 48 kHz so the fallback recorder's synthesised timeline is the
    // real RTP clock, and RESUMED HERE — inside the gesture that led to this
    // call — because a context resumed later stays suspended on Chrome and every
    // downstream symptom looks like a recording bug instead of a policy one.
    try {
      this.ctx = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' })
      await this.ctx.resume()
    } catch (e) {
      this.ctx = null
      this.log('warn', `no AudioContext, so local voice activity will not be timed: ${String(e)}`)
    }
    if (this.ctx) this.greetingClip?.audioReady()
    else this.greetingClip?.audioUnavailable()

    // AFTER the context exists and BEFORE the peer connection, so the assistant
    // stream has somewhere to attach the moment `ontrack` fires.
    if (this.ctx) {
      this.meter = new AudioMeter(this.ctx)
      if (this.mic) this.meter.attach('user', this.mic)
    }

    const path = recordingWanted ? support.recorderPath : 'none'
    if (!recordingWanted) {
      this.notice('Recording is disabled for this session by the server, so no audio is being captured.')
    }
    this.set({ recorderPath: path })
    this.reportRecorderDiagnostic('capture.selected')

    const recorder = new Recorder({
      sessionId,
      mediaUrl,
      mediaReconnectUrl,
      path,
      // The assistant track cannot use the encoded transform (see attachRemote),
      // so it needs an encoder of its own. Prefer WebCodecs Opus; PCM16 is the
      // floor and costs ~13x the bytes for a narrower band.
      assistantFallback: support.have.webCodecsOpus
        ? 'webcodecs-opus'
        : support.have.audioWorklet
          ? 'pcm16'
          : null,
      ctx: this.ctx,
      hooks: {
        onDiagnostic: this.reportRecorderDiagnostic,
        onCounters: (counters: RecorderCounters) => this.set({ counters }),
        onGap: (track, fromSeq, toSeq, reason) => {
          this.log('warn', `recording gap on the ${track} track, chunks ${fromSeq}-${toSeq}: ${reason}`)
          this.send({ t: 'rec.gap', track, fromSeq, toSeq, reason })
        },
        onDone: (track, lastSeq, totalPackets, voicedMs, provenance) =>
          this.send({
            t: 'rec.done',
            track,
            lastSeq,
            totalPackets,
            // Omitted rather than sent as 0 or 'none': the server must be able to
            // tell "not measured" from "measured as nothing".
            ...(voicedMs === null ? {} : { voicedMs }),
            ...(provenance === 'none' ? {} : { provenance }),
          }),
        onAssistantBurst: (rtp, epochMs) => this.telemetry?.noteAssistantBurst(rtp, epochMs),
        onLog: this.log,
      },
    })
    this.recorder = recorder
    try {
      recorder.start()
    } catch (e) {
      this.reportRecorderDiagnostic('recorder.start_failed', recordingErrorName(e))
      this.log('error', `the recorder could not start, so this call will not be recorded: ${String(e)}`)
    }

    // ---- from here on, NOTHING may end the call. ----
    let peer: ReturnType<typeof createPeer>
    try {
      // The remote callback runs synchronously inside `ontrack`, which is where
      // the receiver transform has to be installed. See transport.ts.
      peer = createPeer(this.mic, opts.audioEl, (receiver, stream) => {
        void recorder.attachRemote(receiver, stream).catch((e: unknown) => {
          this.reportRecorderDiagnostic('assistant.capture_failed', recordingErrorName(e))
          this.log('error', `the assistant track will not be recorded: ${String(e)}`)
        })
        // Metering, for anything that draws. Separate from the recorder on purpose:
        // a failure here must cost the visualiser and nothing else, so it is not in
        // that catch. See meter.ts.
        this.meter?.attach('assistant', stream)
        // Kept for live audio, which usually starts long after this fires.
        this.remoteStream = stream
        if (!this.snap.takenOver) this.live?.setAgentStream(stream)
      })
    } catch (e) {
      this.fail(describe(e, 'the peer connection could not be created'))
      return
    }
    this.pc = peer.pc
    peer.pc.onconnectionstatechange = () => this.onPcState()
    peer.pc.oniceconnectionstatechange = () => this.onPcState()

    try {
      await recorder.attachLocal(peer.sender, this.mic)
    } catch (e) {
      this.log('error', `the user track will not be recorded: ${String(e)}`)
      this.reportRecorderDiagnostic('user.capture_failed', recordingErrorName(e))
    }
    try {
      const offer = await makeOffer(peer.pc)
      const answer = await backend.exchangeSdp(sessionId, offer)
      await peer.pc.setRemoteDescription({ type: 'answer', sdp: answer })
    } catch (e) {
      this.fail(describe(e, 'the call could not be connected'))
      return
    }
    this.set({ startStage: 'joining' })

    const telemetry = new Telemetry(peer.pc, {
      onSample: (stats) => this.set({ stats }),
      onSpeech: (state) => {
        // Dropped once teardown starts: a start event racing end() would win
        // against the reset and freeze "listening" under a finished call.
        if (this.ending) return
        this.set({ speaking: { ...this.snap.speaking, user: state === 'start' } })
        // The DO decides what to do about a barge-in; the browser only reports
        // that it happened. Sending `interrupt` from here would let a noisy room
        // cancel the assistant's turn.
      },
      onLog: this.log,
    })
    this.telemetry = telemetry
    telemetry.start()
    if (this.ctx && this.mic) telemetry.attachVad(this.ctx, this.mic)

    this.control = new ControlChannel(controlUrl, controlReconnectUrl, {
      onMessage: (msg) => this.onServerMsg(msg),
      onUp: (up) => {
        this.set({ controlUp: up })
        if (up) {
          if (this.controlDropTimer !== null) {
            clearTimeout(this.controlDropTimer)
            this.controlDropTimer = null
          }
          // Close the story only if one was told: a blip that beat the grace
          // period gets no notice at all, so it gets no epilogue either.
          if (this.controlDropAnnounced && this.snap.phase === 'live') {
            this.controlDropAnnounced = false
            this.notice('Reconnected.')
          }
          return
        }
        // Only a drop DURING the call is news. `end()` closes this socket itself,
        // and reporting that as "dropped … reconnecting" put a warning banner on
        // a call that had ended normally — while ControlChannel.scheduleReconnect
        // was correctly declining to reconnect, so the sentence was false twice.
        if (this.snap.phase !== 'live') return
        // The assistant's speaking state travels on THIS channel and nothing
        // else, so from here on it is a guess — and freezing the last frame is
        // the worst guess: on session fb65ac2f the stop frame died with the
        // socket and the pill said "Speaking" for the rest of the call. Reset
        // to idle; the audio is still audible if it is playing, and the next
        // frame after reconnect corrects the pill either way. The USER half is
        // deliberately left alone — the local VAD keeps it honest without any
        // help from the server. This reset is IMMEDIATE — honesty about state —
        // while the notice below waits out the grace period: telling a human is
        // a different act from correcting a pill.
        this.telemetry?.setAssistantSpeaking(false)
        this.set({ speaking: { ...this.snap.speaking, assistant: false } })
        /**
         * SAY NOTHING FOR THE FIRST FEW SECONDS. Most drops reconnect on the
         * first or second dial (backoff starts at 250ms), the conversation is
         * genuinely unaffected while they do — media is browser↔OpenAI — and a
         * scary sentence about a channel the caller has never heard of, shown
         * during a blip that self-heals in under a second, reads as the product
         * failing (session 59ef61e4 landed one mid-demo). So the notice fires
         * only when the outage outlives the grace period — long enough that
         * transcripts and cards visibly stalling need an explanation — worded
         * around what still works, and ONCE per outage: every failed re-dial
         * lands here again via onclose, and each used to be eligible to log.
         */
        if (this.controlDropTimer === null && !this.controlDropAnnounced) {
          this.controlDropTimer = setTimeout(() => {
            this.controlDropTimer = null
            if (this.snap.controlUp || this.snap.phase !== 'live') return
            this.controlDropAnnounced = true
            console.warn('[call] control channel down past grace period; reconnecting')
            this.notice('Reconnecting… the conversation is not affected.')
          }, CONTROL_DROP_NOTICE_MS)
        }
      },
      onClock: (clock) => this.set({ clock }),
      lastSeq: () => this.lastSeq,
      onLog: this.log,
    })
    this.control.start()

    this.flushTimer = setInterval(() => this.flushTelemetry(false), TELEMETRY_FLUSH_MS)
    this.capTimers.push(
      setTimeout(() => {
        this.notice(
          'This session is approaching the provider’s 60-minute cap and will end automatically in five minutes.',
        )
      }, CAP_WARN_MS),
      setTimeout(() => {
        this.notice('Ending the call: the provider’s 60-minute session cap is about to be reached.')
        void this.end()
      }, CAP_END_MS),
    )

    this.set({ phase: 'live', startedAtMs: nowEpochMs(), startStage: null })
  }

  // --------------------------------------------------------------------------
  // Server events
  // --------------------------------------------------------------------------

  private onServerMsg(msg: ServerMsg): void {
    switch (msg.t) {
      case 'ready': {
        // The server's ackSeq is the authority on what it has. Handing it to the
        // recorder is the entire resume protocol.
        this.lastSeq = { ...msg.ackSeq }
        this.recorder?.open(msg.ackSeq)
        this.flushRecorderDiagnostics()
        this.flushGreetingReports()
        this.set({ sessionId: msg.sessionId })
        if (!msg.recording && this.snap.recorderPath !== 'none') {
          this.notice('The server is not accepting a recording for this session.')
        }
        break
      }
      case 'speech':
        // Same rule as the local VAD callback: once teardown starts, a racing
        // start event must not resurrect the indicator under a finished call.
        if (this.ending) break
        if (msg.who === 'assistant') {
          this.telemetry?.setAssistantSpeaking(msg.state === 'start')
          this.set({ speaking: { ...this.snap.speaking, assistant: msg.state === 'start' } })
        } else {
          // The provider's own VAD, which is the authoritative one. Our local
          // energy check is only used for turn marks and for the level meter.
          this.set({ speaking: { ...this.snap.speaking, user: msg.state === 'start' } })
        }
        break
      case 'transcript':
        this.mergeTranscript(msg.itemId, msg.who, msg.text, msg.final)
        break
      case 'greeting_render': {
        const { t: _t, ...outcome } = msg
        this.set({ greetingRender: outcome })
        break
      }
      case 'tool':
        this.mergeTool(msg.callId, msg.name, msg.state, msg.displayName ?? null, msg.displayDescription ?? null)
        break
      case 'card':
        this.mergeCard(msg.callId, msg.title, msg.body)
        break
      case 'ack': {
        this.lastSeq = { ...this.lastSeq, [msg.track]: msg.seq }
        this.recorder?.ack(msg.track, msg.seq)
        break
      }
      case 'live':
        if (msg.on) this.startLive()
        else this.stopLive()
        break
      case 'takeover':
        // `ended` is reserved for a hand-back to the AI, which does not exist yet.
        if (msg.state === 'started') this.onTakeover()
        break
      case 'state':
        this.set({ serverState: msg.state, serverStateDetail: msg.detail ?? null })
        // The state says it as well as the takeover message, and a control
        // socket that reconnects mid-takeover gets the state again: either one
        // is enough, and both are harmless.
        if (msg.state === 'TAKEN_OVER') this.onTakeover()
        if (ENDS_THE_CALL.has(msg.state)) {
          // The provider or the DO ended it. Tear down locally, but do not
          // report it as our failure.
          if (this.snap.phase === 'live') void this.end()
        }
        break
      case 'error':
        this.log(msg.fatal ? 'error' : 'warn', `${msg.code}: ${msg.message}`)
        if (msg.fatal) this.set({ error: `${msg.code}: ${msg.message}` })
        break
      case 'pong':
        // Handled inside the control channel, which owns the clock estimate.
        break
    }
  }

  /**
   * Fold one `transcript` control message into the visible lines. The rule —
   * a delta appends, a final replaces, an empty final never blanks the line, a
   * delta after the final is dropped — lives in transcript-merge.ts, shared
   * with the panel's live console, and its reasons are written down there.
   */
  private mergeTranscript(itemId: string, who: TrackName, text: string, final: boolean): void {
    const next = mergeTranscriptLine(this.snap.transcript, itemId, who, text, final, nowEpochMs(), MAX_TRANSCRIPT_LINES)
    if (next) this.set({ transcript: next })
  }

  private mergeTool(
    callId: string,
    name: string,
    state: ToolActivity['state'],
    displayName: string | null,
    displayDescription: string | null,
  ): void {
    const tools = this.snap.tools.slice()
    const at = tools.findIndex((t) => t.callId === callId)
    if (at >= 0) {
      const existing = tools[at]
      if (existing) {
        tools[at] = {
          ...existing,
          state,
          endedAtMs: state === 'running' ? existing.endedAtMs : nowEpochMs(),
        }
      }
    } else {
      tools.push({
        callId,
        name,
        displayName,
        displayDescription,
        state,
        startedAtMs: nowEpochMs(),
        endedAtMs: state === 'running' ? null : nowEpochMs(),
        // Anchored to the conversation as it stands right now — the transcript
        // renders this chip after the line that was last on screen when the
        // model reached for the tool.
        anchorIndex: this.snap.transcript.length,
      })
    }
    this.set({ tools })
  }

  /**
   * Fold one `{t:'card'}` control message into the snapshot — the `show_info`
   * screen card, following `mergeTool`'s shape because it is the same problem.
   *
   * DEDUPED BY callId, REPLACING RATHER THAN APPENDING. A re-attached sideband
   * can replay the tail of a stream, and the second copy of a card frame must
   * update the one already on screen, not stack a twin under it — the same
   * idempotence-under-replay rule the transcript's finals follow. On a replace
   * the ORIGINAL `atMs` and `anchorIndex` are kept: the card's place in the
   * conversation is where it first appeared, and a replayed frame arriving after
   * ten more lines must not teleport it to the bottom.
   */
  private mergeCard(callId: string, title: string | null, body: string): void {
    const cards = this.snap.cards.slice()
    const at = cards.findIndex((c) => c.callId === callId)
    if (at >= 0) {
      const existing = cards[at]
      if (existing) cards[at] = { ...existing, title, body }
    } else {
      cards.push({
        callId,
        title,
        body,
        atMs: nowEpochMs(),
        // Anchored to the conversation as it stands right now, exactly like a
        // tool chip: the card renders after the line that was last on screen
        // when the model chose to show it.
        anchorIndex: this.snap.transcript.length,
      })
    }
    this.set({ cards })
  }

  private onPcState(): void {
    const pc = this.pc
    if (!pc) return
    this.set({ pc: { connection: pc.connectionState, ice: pc.iceConnectionState } })
    if (pc.connectionState === 'failed') {
      // After a takeover this is expected, and it is not the call: the DO hung
      // up the provider's leg on purpose, and the caller now talks to a person
      // over the live socket. Ending here would hang up on that person.
      if (this.snap.takenOver) return
      // Otherwise this one IS fatal: the media path is the call.
      this.set({ error: 'The media connection failed. This is the audio path itself, so the call is over.' })
      if (this.snap.phase === 'live') void this.end()
    }
  }

  // --------------------------------------------------------------------------
  // Live listen-in and takeover (docs/hitl.md §7)
  // --------------------------------------------------------------------------

  /** Somebody in the panel is listening: open the live socket. Idempotent. */
  private startLive(): void {
    if (this.ending || this.snap.phase !== 'live') return
    if (this.live) {
      this.live.start()
      return
    }
    const ctx = this.ctx
    const mic = this.mic
    if (!this.liveUrl || !ctx || !mic) {
      // Once per call: the DO asks again on every console that joins, and the
      // answer does not change. The DO tells the listener audio is unavailable.
      if (!this.liveRefusalLogged) {
        this.liveRefusalLogged = true
        console.warn(
          `[call] live audio was asked for but cannot be sent: ${
            !this.liveUrl ? 'the server gave this call no live-audio address' : 'there is no AudioContext or microphone'
          }`,
        )
      }
      return
    }
    this.live = new LiveLink({
      url: this.liveUrl,
      ctx,
      mic,
      micSilenced: () => this.snap.muted || this.greetingHold,
      onLog: this.liveLog,
    })
    this.live.start()
    if (this.remoteStream && !this.snap.takenOver) this.live.setAgentStream(this.remoteStream)
  }

  private stopLive(): void {
    if (this.snap.takenOver && this.live) {
      // The DO is the authority and is obeyed, but this cuts the person off.
      console.warn('[call] live audio was turned off while a person holds the call')
    }
    this.live?.close()
    this.live = null
  }

  /**
   * A PERSON HAS THE CALL. The DO is hanging up the AI leg; from here the
   * person's voice arrives on the live socket and plays in place of the
   * agent's. Idempotent, because both the takeover message and the state say it.
   */
  private onTakeover(): void {
    if (this.snap.takenOver || this.ending || this.snap.phase !== 'live') return
    this.set({ takenOver: true, speaking: { ...this.snap.speaking, assistant: false } })
    this.telemetry?.setAssistantSpeaking(false)
    // The agent's voice, in both forms: a greeting still playing, and the
    // provider's audio. The element is muted rather than detached, so the
    // recorder's fallback tap (which needs it attached, see transport.ts) is
    // not disturbed while the provider's leg winds down.
    this.greetingClip?.stop()
    this.greetingClip = null
    if (this.audioEl) {
      this.audioElWasMuted = this.audioEl.muted
      this.audioEl.muted = true
    }
    const live = this.live
    if (!live) {
      this.log('warn', 'A person took over this call, but live audio is not connected, so their voice cannot be heard here.')
      return
    }
    live.stopAgent()
    void live.playHuman().then((stream) => {
      // The level meter follows the person, so a visualiser keeps moving.
      if (stream && this.snap.takenOver && !this.ending) this.meter?.attach('assistant', stream)
    })
    console.info('[call] a person has taken over this call')
  }

  /** Live audio is a copy: its trouble goes to the console, not onto the screen. */
  private liveLog = (level: 'info' | 'warn' | 'error', message: string): void => {
    if (level === 'info') console.info(`[call] ${message}`)
    else console.warn(`[call] ${message}`)
  }

  // --------------------------------------------------------------------------
  // Outbound
  // --------------------------------------------------------------------------

  private send(msg: ClientMsg): void {
    this.control?.send(msg)
  }

  private flushTelemetry(final: boolean): void {
    const t = this.telemetry
    if (!t) return
    const payload = t.drain(final)
    if (!payload.turns && !payload.stats) return
    this.send({ t: 'telemetry', ...payload })
    this.set({ turns: t.turns.slice() })
  }

  interrupt(): void {
    this.send({ t: 'intent', intent: 'interrupt' })
  }

  sendText(text: string): void {
    const trimmed = text.trim()
    if (trimmed.length === 0) return
    this.send({ t: 'text', text: trimmed })
    // Echo locally with a synthetic id so the human sees what they sent even
    // though the DO will not send it back as a transcript item.
    this.mergeTranscript(`local-${Date.now()}`, 'user', trimmed, true)
  }

  setMuted(muted: boolean): void {
    this.set({ muted })
    this.applyMicEnabled()
    this.send({ t: 'intent', intent: muted ? 'mute' : 'unmute' })
  }

  /**
   * The mic is live only when the human has not muted it AND no greeting clip
   * is playing. One place decides, so unmuting during the greeting cannot open
   * the mic early and letting the greeting end cannot unmute a muted caller.
   * The peer sends the mic's own track, so this is also what the provider hears.
   */
  private applyMicEnabled(): void {
    const enabled = !(this.snap.muted || this.greetingHold)
    for (const track of this.mic?.getAudioTracks() ?? []) track.enabled = enabled
  }

  /** Decode and start the clip through the call's own AudioContext — resumed
   *  inside the click, so it plays on iOS without another gesture. */
  private async playGreetingClip(bytes: ArrayBuffer): Promise<ClipPlayback> {
    const ctx = this.ctx
    if (!ctx) throw new Error('no AudioContext')
    const buffer = await ctx.decodeAudioData(bytes)
    const src = ctx.createBufferSource()
    src.buffer = buffer
    src.connect(ctx.destination)
    const ended = new Promise<void>((resolve) => {
      src.onended = () => resolve()
    })
    src.start()
    return {
      durationMs: Math.round(buffer.duration * 1000),
      ended,
      stop: () => {
        try {
          src.stop()
        } catch {
          /* already stopped */
        }
      },
    }
  }

  private sendGreetingReport(report: GreetingClipReport): void {
    const msg: ClientMsg = { t: 'greeting', ...report }
    // Queued behind anything already waiting, so the server sees them in order.
    if (this.greetingOutbox.length > 0 || !this.control?.send(msg)) this.greetingOutbox.push(msg)
  }

  private flushGreetingReports(): void {
    while (this.greetingOutbox.length > 0) {
      if (!this.control?.send(this.greetingOutbox[0]!)) return
      this.greetingOutbox.shift()
    }
  }

  // --------------------------------------------------------------------------
  // End
  // --------------------------------------------------------------------------

  /**
   * THE ORDER OF THIS METHOD IS THE POINT, so it is written out here.
   *
   *   1. stop being a call — timers, telemetry, and the peer connection. The
   *      human hears silence and the microphone light goes out immediately, which
   *      is the whole of what "the call ended" means to them.
   *   2. finish producing and shipping bytes — `recorder.stop()`, bounded.
   *   3. ONLY THEN tell the server to finalise.
   *
   * WHY `end_call` MOVED TO THE END. It used to be the first statement in this
   * method, and server-side it runs straight through: hang up, flush staging,
   * compute the verdict, write Neon, enqueue the finalize job. That is one to two
   * seconds. Meanwhile the recorder had not been stopped yet — the final chunk of
   * each track is produced by `Chunker.flush(true)` inside `recorder.stop()`, and
   * the uploader then drains for up to six seconds. So the tail of the recording
   * was racing the finalisation of the recording, and it only usually won because
   * `endCall` happens to await an HTTP round trip to the provider first. On the
   * paths where it does not hang up — `provider_404`, `sideband_unrecoverable`,
   * `never_started` — the flush runs within a millisecond and the tail loses. The
   * final object is written with `If-None-Match: *` onto a prefix where DELETE is
   * IAM-denied, so an object muxed from a short inventory can never be repaired:
   * this is not a race that can be papered over downstream.
   *
   * WHAT IS NOT SENT EARLY TO COMPENSATE. Not `interrupt`: the DO answers it with
   * `output_audio_buffer.clear`, the provider replies `output_audio_buffer.cleared`,
   * and `turns.ts` records that as a CANCELLED RESPONSE. Ending a call would then
   * manufacture a barge-in in the data that T4's calibration is meant to measure.
   * Closing the peer connection already stops the audio in the only place the user
   * can hear it.
   *
   * THE COST, STATED: the provider's call stays up for the length of the drain,
   * normally a few hundred milliseconds, at worst the recorder's nine-second
   * budget. And if the tab is closed DURING the drain, neither end signal is sent
   * and the session resolves as ENDED_UNKNOWN via the sweeper — the same outcome a
   * tab close has always had, since nothing calls this method on unload.
   */
  async end(): Promise<void> {
    if (this.ending) return
    this.ending = true
    const wasLive = this.snap.phase === 'live'
    const sessionId = this.snap.sessionId
    // Speaking flags are cleared HERE and not left to the sockets: they are set
    // by start/stop events, and tearing the sockets down mid-utterance means the
    // matching stop never arrives — which left "the assistant is speaking" on
    // screen under a call that had ended.
    this.set({ phase: 'ending', speaking: { user: false, assistant: false } })
    // A greeting still playing stops with the call; its report no longer matters.
    this.greetingClip?.stop()
    this.greetingClip = null
    this.greetingOutbox = []

    this.flushTelemetry(true)

    for (const t of this.capTimers) clearTimeout(t)
    this.capTimers = []
    if (this.flushTimer !== null) clearInterval(this.flushTimer)
    this.flushTimer = null
    // A pending drop notice must not fire over a call that is ending on
    // purpose; its own phase guard would catch most of that window, but a
    // cleared timer catches all of it.
    if (this.controlDropTimer !== null) clearTimeout(this.controlDropTimer)
    this.controlDropTimer = null
    this.controlDropAnnounced = false

    this.telemetry?.stop()

    // The live copy and, after a takeover, the person's voice: part of "stop
    // being a call", so they go with the peer connection and before the drain.
    this.live?.close()
    this.live = null
    this.remoteStream = null
    if (this.snap.takenOver && this.audioEl) this.audioEl.muted = this.audioElWasMuted

    // BEFORE the peer connection goes, so the analyser nodes are disconnected from
    // streams that still exist. Disconnecting after the tracks have ended is
    // harmless in every browser tested, but the ordering costs nothing and the
    // alternative relies on that staying true.
    this.meter?.dispose()
    this.meter = null

    // The peer connection closes BEFORE the recorder stops, so the transform
    // streams end and the sink sees a real end-of-track rather than a truncation.
    if (this.pc) teardown(this.pc, this.mic)
    this.pc = null
    this.mic = null

    // A drain long enough to notice deserves a word. The button already reads
    // "Finishing…"; this says what is being finished, and only if it is slow.
    const slowTail = setTimeout(() => {
      this.notice('Still uploading the last seconds of audio before the recording is finalised.')
    }, TAIL_NOTICE_MS)
    try {
      await this.recorder?.stop()
    } catch (e) {
      this.log('warn', `the recorder did not shut down cleanly: ${String(e)}`)
      this.reportRecorderDiagnostic('recorder.stop_failed', recordingErrorName(e))
    } finally {
      clearTimeout(slowTail)
    }
    this.recorder = null

    // Anything the recorder's final flush taught the telemetry — a last assistant
    // burst, the closing turn — while the control socket is still open.
    this.flushTelemetry(true)

    /**
     * NOW the server may finalise. Both signals, deliberately.
     *
     * The `intent` rides the control socket, which is exactly the thing most
     * likely to be gone at the moment a user gives up on a call. If it is down
     * the message goes nowhere and the DO learns nothing; it then watches its
     * sideband EOF, cannot distinguish a hangup from a fault, writes
     * `ENDED_UNKNOWN`, and the server-side sweeper resolves it five minutes
     * later. A user pressing "End" deserves better than a five-minute question
     * mark.
     *
     * `POST /api/sessions/:id/end` reaches the DO through edge-api instead, so it
     * survives a dead control socket. It is IDEMPOTENT — the DO claims the
     * finalisation with a check-and-set — so both signals arriving is a no-op,
     * and that is the normal case rather than the exception.
     *
     * The intent goes first because it is in-order behind `rec.done` on the same
     * socket, so the DO has the client's own account of what it sent before it
     * decides what it holds. The HTTP call is NOT awaited: it is a fallback for a
     * socket that is already dead, and the local teardown has no business
     * blocking on a network round trip.
     */
    if (wasLive) this.send({ t: 'intent', intent: 'end_call' })
    if (wasLive && sessionId !== null) {
      // Fire and forget by contract — see `endSession` in backend.ts. The
      // implementation swallows its own failures, so there is nothing to catch
      // here and nothing to await: this is a fallback for a socket that is already
      // dead, and the local teardown has no business blocking on a round trip.
      this.backend.endSession(sessionId, 'client_end_button')
    }

    // The control socket closes LAST, so `rec.done`, the final telemetry and the
    // `end_call` intent actually get out.
    //
    // But closing it means the DO's own `{t:'state', state:'ENDED_NORMAL'}` may
    // never reach us — it is sent after the DO finishes hanging up, flushing and
    // enqueuing, which is after we have gone. So the last state we were TOLD is
    // still `LIVE`, and the panel went on displaying "live" next to a "Start
    // another call" button. Keeping a stale LIVE is the one thing this UI must not
    // do: it reports a call as in progress on the strength of a message that can
    // no longer arrive. Cleared to "not reported", which is the truth — the
    // authoritative final state is on the session page, written by the DO.
    // TAKEN_OVER is a live state too, and just as stale once we have gone.
    const lastState = this.snap.serverState
    if (lastState !== null && (lastState.startsWith('LIVE') || lastState === 'TAKEN_OVER')) {
      this.set({
        serverState: null,
        serverStateDetail: 'we ended the call locally before the server reported its final state',
      })
    }
    this.control?.close()
    this.control = null

    if (this.ctx) {
      try {
        await this.ctx.close()
      } catch {
        /* a context that will not close is harmless at this point */
      }
      this.ctx = null
    }

    this.set({ phase: this.snap.error ? 'failed' : 'ended', endedAtMs: nowEpochMs(), controlUp: false })
    this.ending = false
  }

  private fail(message: string): void {
    this.set({ error: message })
    void this.end().then(() => this.set({ phase: 'failed' }))
  }
}

function describe(e: unknown, fallback: string): string {
  if (e instanceof Error) return `${fallback}: ${e.message}`
  return `${fallback}: ${String(e)}`
}
